import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { api } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import { Icon } from '@/components/Icon';
import { StatusPill } from '@/components/StatusPill';
import { Topbar } from '@/components/Topbar';
import { EmptyState } from '@/components/EmptyState';
import { haptic } from '@/lib/telegram';
import type { DialogStep, DialogTemplate, ManualDialogStepInput } from '@/types/domain';

export function CampaignDialogsPage(): JSX.Element {
  const { id } = useParams();
  const cid = Number(id);
  const { data, loading, reload } = useAsync(() => api.listDialogs(cid), [cid]);
  const [hint, setHint] = useState('');
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    haptic('medium');
    await api.generateDialogs(cid, 5);
    setBusy(false);
    setHint('');
    reload();
  };

  const createManual = async (steps: ManualDialogStepInput[]) => {
    setCreateBusy(true);
    try {
      await api.createManualDialog(cid, steps);
      haptic('success');
      setCreateOpen(false);
      await reload();
    } finally {
      setCreateBusy(false);
    }
  };

  // Автогенерация: если кампания только-только создана и диалогов 0 — запускаем сразу 5
  // в фоне, чтобы пользователь сразу попал в режим «ревью» вместо пустого экрана.
  const autoTriggered = useRef(false);
  useEffect(() => {
    if (loading || busy || autoTriggered.current) return;
    if ((data ?? []).length === 0) {
      autoTriggered.current = true;
      generate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data]);

  return (
    <div className="space-y-4">
      <Topbar
        title="Диалоги"
        subtitle="генерация · ревью"
        right={
          <button className="btn-ghost" onClick={() => setCreateOpen(true)}>
            Написать свой
          </button>
        }
      />

      {loading && (<div className="space-y-3"><div className="skeleton h-44" /><div className="skeleton h-44" /></div>)}

      {!loading && busy && (data ?? []).length === 0 && (
        <div className="card flex flex-col items-center gap-3 p-8 text-center">
          <span className="dot dot-pulse bg-accent-300" style={{height:10,width:10}} />
          <div className="text-base font-semibold text-ink-50">Генерируем 5 диалогов</div>
          <div className="max-w-[280px] text-sm text-ink-300">Готовим черновики на основе вашего брифа. Через пару секунд появятся карточки для ревью.</div>
        </div>
      )}
      {!loading && !busy && (data ?? []).length === 0 && (
        <EmptyState
          icon="sparkles"
          title="Не получилось сгенерировать"
          body="Похоже, не задан бриф. Заполните его на экране Промпты — и попробуйте снова."
          cta={
            <div className="flex w-full gap-2">
              <button className="btn-ghost flex-1" onClick={() => setCreateOpen(true)}>Написать свой</button>
              <button className="btn-primary flex-1" onClick={generate}>Попробовать ещё раз</button>
            </div>
          }
        />
      )}

      <ul className="space-y-3">
        {(data ?? []).map((tpl) => (
          <DialogCard key={tpl.id} tpl={tpl} onChange={reload} />
        ))}
      </ul>

      {(data ?? []).length > 0 && (
        <div className="card p-4">
          <div className="mb-2 flex items-center justify-between gap-3">
            <div className="text-sm font-semibold text-ink-50">Догенерировать ещё</div>
            <button className="btn-ghost shrink-0" onClick={() => setCreateOpen(true)}>
              Написать свой
            </button>
          </div>
          <input
            className="input"
            placeholder="(опц.) подсказка ИИ: «больше юмора», «без рефки», …"
            value={hint}
            onChange={(e) => setHint(e.target.value)}
          />
          <button className="btn-primary mt-3 w-full" disabled={busy} onClick={generate}>
            <Icon name="sparkles" size={16} /> {busy ? 'Генерирую…' : 'Сгенерировать ещё 5'}
          </button>
        </div>
      )}

      {createOpen ? (
        <ManualDialogModal
          busy={createBusy}
          onClose={() => {
            if (!createBusy) setCreateOpen(false);
          }}
          onSave={createManual}
        />
      ) : null}
    </div>
  );
}

function DialogCard({ tpl, onChange }: { tpl: DialogTemplate; onChange: () => void }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const action = async (kind: 'approve' | 'reject' | 'regen') => {
    setBusy(true);
    haptic(kind === 'approve' ? 'success' : kind === 'reject' ? 'warning' : 'light');
    if (kind === 'approve') await api.approveDialog(tpl.id);
    if (kind === 'reject') await api.rejectDialog(tpl.id);
    if (kind === 'regen') await api.regenerateDialog(tpl.id);
    setBusy(false);
    onChange();
  };

  return (
    <div className="card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-2xs uppercase tracking-widest text-ink-500">диалог #{tpl.id}</div>
        <StatusPill status={tpl.status} />
      </div>

      <div className="space-y-2">
        {tpl.steps.map((s) => <Bubble key={s.id} step={s} />)}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <button onClick={() => action('reject')} disabled={busy} className="btn-ghost text-bad">
          <Icon name="x" size={16} /> Отклонить
        </button>
        <button onClick={() => action('regen')} disabled={busy} className="btn-ghost">
          <Icon name="refresh" size={16} /> Перегенерить
        </button>
        <button onClick={() => action('approve')} disabled={busy} className="btn-primary">
          <Icon name="check" size={16} /> Принять
        </button>
      </div>
    </div>
  );
}

function Bubble({ step }: { step: DialogStep }): JSX.Element {
  const isA = step.role === 'A';
  return (
    <div className={`flex gap-2 ${isA ? '' : 'flex-row-reverse'}`}>
      <div
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-2xs font-bold ${
          isA ? 'bg-ink-800 text-ink-300' : 'bg-grad-primary text-white'
        }`}
      >
        {step.role}
      </div>
      <div
        className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
          isA
            ? 'rounded-tl-md bg-ink-800 text-ink-100'
            : 'rounded-tr-md bg-accent-500/15 text-ink-50 border border-accent-500/25'
        } ${step.isPitch ? 'ring-1 ring-accent-500/40' : ''}`}
      >
        {step.text}
        {step.isPitch && (
          <div className="mt-1 inline-flex items-center gap-1 rounded-pill bg-accent-500/20 px-2 py-0.5 text-2xs text-accent-300">
            <Icon name="flag" size={10} /> CTA
          </div>
        )}
      </div>
    </div>
  );
}

type ManualDraftStep = {
  role: 'A' | 'B';
  text: string;
  isPitch: boolean;
};

function ManualDialogModal({
  busy,
  onClose,
  onSave,
}: {
  busy: boolean;
  onClose: () => void;
  onSave: (steps: ManualDialogStepInput[]) => Promise<void>;
}): JSX.Element {
  const [steps, setSteps] = useState<ManualDraftStep[]>([
    { role: 'A', text: '', isPitch: false },
    { role: 'B', text: '', isPitch: false },
  ]);

  const updateStep = (index: number, patch: Partial<ManualDraftStep>) => {
    setSteps((current) => current.map((step, idx) => (idx === index ? { ...step, ...patch } : step)));
  };

  const addStep = () => {
    setSteps((current) => [
      ...current,
      { role: current[current.length - 1]?.role === 'A' ? 'B' : 'A', text: '', isPitch: false },
    ]);
  };

  const removeStep = (index: number) => {
    setSteps((current) => current.filter((_, idx) => idx !== index));
  };

  const canSave =
    steps.length > 0 &&
    steps.every((step, index) => (index === 0 || (step.role === 'A' || step.role === 'B')) && step.text.trim());

  const submit = async () => {
    if (!canSave || busy) return;
    await onSave(
      steps.map((step, index) => ({
        role: index === 0 ? 'A' : step.role,
        text: step.text.trim(),
        replyToIdx: index === 0 ? null : index - 1,
        isPitch: step.isPitch,
      })),
    );
  };

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-[32px] border border-white/10 bg-[#0c1020] p-5 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mx-auto mb-3 h-1 w-12 rounded-full bg-white/10" />
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[17px] font-semibold text-white">Свой диалог</div>
            <div className="mt-1 text-[13px] text-[#7d85a9]">
              Первый шаг всегда от `A`. Каждый следующий шаг по умолчанию будет reply на предыдущий.
            </div>
          </div>
          <button className="btn-ghost shrink-0" onClick={onClose}>Закрыть</button>
        </div>

        <div className="mt-4 space-y-3">
          {steps.map((step, index) => (
            <div key={index} className="rounded-[26px] border border-white/10 bg-white/[0.03] p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="text-sm font-semibold text-white">Шаг {index + 1}</div>
                {steps.length > 1 ? (
                  <button
                    className="btn-ghost shrink-0 text-bad"
                    disabled={busy || index === 0}
                    onClick={() => removeStep(index)}
                  >
                    Удалить
                  </button>
                ) : null}
              </div>

              <div className="mb-3 flex flex-wrap gap-2">
                {index === 0 ? (
                  <span className="pill pill-primary">A · старт</span>
                ) : (
                  <>
                    {(['A', 'B'] as const).map((role) => (
                      <button
                        key={role}
                        type="button"
                        className={`rounded-2xl border px-3 py-1.5 text-xs font-medium transition ${
                          step.role === role
                            ? 'border-accent-300 bg-accent-300/15 text-accent-100'
                            : 'border-ink-700 bg-ink-900/70 text-ink-300'
                        }`}
                        onClick={() => updateStep(index, { role })}
                      >
                        {role}
                      </button>
                    ))}
                  </>
                )}
                <button
                  type="button"
                  className={`rounded-2xl border px-3 py-1.5 text-xs font-medium transition ${
                    step.isPitch
                      ? 'border-accent-300 bg-accent-300/15 text-accent-100'
                      : 'border-ink-700 bg-ink-900/70 text-ink-300'
                  }`}
                  onClick={() => updateStep(index, { isPitch: !step.isPitch })}
                >
                  CTA
                </button>
              </div>

              <textarea
                className="textarea min-h-[104px] w-full rounded-[22px] border border-white/10 bg-white/4 px-4 py-4 text-[14px] text-white outline-none"
                placeholder={index === 0 ? 'Стартовая реплика A…' : 'Следующая реплика…'}
                value={step.text}
                onChange={(event) => updateStep(index, { text: event.target.value })}
              />
            </div>
          ))}
        </div>

        <button className="btn-ghost mt-4 w-full" onClick={addStep} disabled={busy || steps.length >= 10}>
          <Icon name="plus" size={16} /> Добавить реплику
        </button>

        <div className="mt-4 flex gap-2">
          <button className="btn-ghost flex-1" onClick={onClose} disabled={busy}>Отмена</button>
          <button className="btn-primary flex-1" onClick={submit} disabled={busy || !canSave}>
            {busy ? 'Сохраняю…' : 'Сохранить черновик'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
